import { createAsyncThunk } from "@reduxjs/toolkit"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IOneVerifyCreateSavedFilterData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import { IOneVerifyReportSavedFilter } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ReportSavedFiltersService } from "../../services/reportSavedFilters.service"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

/**
 * Get list of saved filters
 */
export const reportSavedFiltersGetList = createAsyncThunk<
    {
        reportType: PoliciesListType
        items: IOneVerifyReportSavedFilter[]
    },
    IThunkActionWithPracticeData<{
        reportType: PoliciesListType
    }>
>("reportSavedFiltersGetListPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("reportSavedFiltersGetListLoading"))

    const reportType = action.payload.reportType

    try {
        const result = await ReportSavedFiltersService.getList(
            action.practice,
            reportType,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return {
            reportType,
            items: result || [],
        }
    } catch (e) {
        return {
            reportType,
            items: [],
        }
    } finally {
        action.onFinally?.()

        dispatch(runtimeStopLoading("reportSavedFiltersGetListLoading"))
    }
})

/**
 * Create saved filter
 */
export const reportSavedFiltersCreate = createAsyncThunk<
    {
        reportType: PoliciesListType
        item?: IOneVerifyReportSavedFilter
    },
    IThunkActionWithPracticeData<IOneVerifyCreateSavedFilterData>
>("reportSavedFiltersCreatePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("reportSavedFiltersCreateLoading"))

    const reportType = action.payload.reportType

    try {
        const result = await ReportSavedFiltersService.create(
            action.practice,
            action.payload,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return {
            reportType,
            item: result,
        }
    } catch (e) {
        return {
            reportType,
        }
    } finally {
        action.onFinally?.()

        dispatch(runtimeStopLoading("reportSavedFiltersCreateLoading"))
    }
})

/**
 * Delete saved filter
 */
export const reportSavedFiltersDelete = createAsyncThunk<
    {
        reportType: PoliciesListType
        deletedId?: number
    },
    IThunkActionWithPracticeData<{
        reportType: PoliciesListType
        filterId: number
    }>
>("reportSavedFiltersDeletePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("reportSavedFiltersDeleteLoading"))

    const reportType = action.payload.reportType

    try {
        const result = await ReportSavedFiltersService.delete(
            action.practice,
            action.payload.filterId,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return {
            reportType,
            deletedId: result?.deletedId,
        }
    } catch (e) {
        return {
            reportType,
        }
    } finally {
        action.onFinally?.()

        dispatch(runtimeStopLoading("reportSavedFiltersDeleteLoading"))
    }
})
